<script setup lang="ts">
import type { HeadingLevel } from "@pixelpoint-pattern-library-v2/base/types/index.ts";
import { HPixHeading } from "@pixelpoint-pattern-library-v2/base/components/headless/base/primitives/HPixHeading/index.ts";

type Props = {
  level?: HeadingLevel;
  as?: "h" | "p" | "div" | "span";
  appearAs?: HeadingLevel;
  class?: string | string[];
};

const props = withDefaults(defineProps<Props>(), {
  as: "h",
  level: 2,
});
</script>

<template>
  <HPixHeading
    :as="props.as"
    :level="props.level"
    class="pix-heading"
    :class="props.class"
    :data-appear-as="props.appearAs ?? undefined"
  >
    <slot></slot>
  </HPixHeading>
</template>

<style>
@layer pix-components {
  .pix-heading {
    text-wrap: balance;
    color: var(--color-heading);
  }

  /* Default styling based on semantic level (data-level set by HPixHeading) */
  .pix-heading[data-level="1"] {
    font-size: var(--text-h1);
    line-height: var(--text-h1--line-height);
    font-family: var(--font-h1);
    font-weight: var(--font-weight-h1);
    margin-bottom: var(--spacing-h1);
  }

  .pix-heading[data-level="2"] {
    font-size: var(--text-h2);
    line-height: var(--text-h2--line-height);
    font-family: var(--font-h2);
    font-weight: var(--font-weight-h2);
    margin-bottom: var(--spacing-h2);
  }

  .pix-heading[data-level="3"] {
    font-size: var(--text-h3);
    line-height: var(--text-h3--line-height);
    font-family: var(--font-h3);
    font-weight: var(--font-weight-h3);
    margin-bottom: var(--spacing-h3);
  }

  .pix-heading[data-level="4"] {
    font-size: var(--text-h4);
    line-height: var(--text-h4--line-height);
    font-family: var(--font-h4);
    font-weight: var(--font-weight-h4);
    margin-bottom: var(--spacing-h4);
  }

  .pix-heading[data-level="5"] {
    font-size: var(--text-h5);
    line-height: var(--text-h5--line-height);
    font-family: var(--font-h5);
    font-weight: var(--font-weight-h5);
    margin-bottom: var(--spacing-h5);
  }

  .pix-heading[data-level="6"] {
    font-size: var(--text-h6);
    line-height: var(--text-h6--line-height);
    font-family: var(--font-h6);
    font-weight: var(--font-weight-h6);
    margin-bottom: var(--spacing-h6);
  }

  /* appearAs overrides: visual styling level takes precedence over semantic level */
  .pix-heading[data-appear-as="1"] {
    font-size: var(--text-h1);
    line-height: var(--text-h1--line-height);
    font-family: var(--font-h1);
    font-weight: var(--font-weight-h1);
    margin-bottom: var(--spacing-h1);
  }

  .pix-heading[data-appear-as="2"] {
    font-size: var(--text-h2);
    line-height: var(--text-h2--line-height);
    font-family: var(--font-h2);
    font-weight: var(--font-weight-h2);
    margin-bottom: var(--spacing-h2);
  }

  .pix-heading[data-appear-as="3"] {
    font-size: var(--text-h3);
    line-height: var(--text-h3--line-height);
    font-family: var(--font-h3);
    font-weight: var(--font-weight-h3);
    margin-bottom: var(--spacing-h3);
  }

  .pix-heading[data-appear-as="4"] {
    font-size: var(--text-h4);
    line-height: var(--text-h4--line-height);
    font-family: var(--font-h4);
    font-weight: var(--font-weight-h4);
    margin-bottom: var(--spacing-h4);
  }

  .pix-heading[data-appear-as="5"] {
    font-size: var(--text-h5);
    line-height: var(--text-h5--line-height);
    font-family: var(--font-h5);
    font-weight: var(--font-weight-h5);
    margin-bottom: var(--spacing-h5);
  }

  .pix-heading[data-appear-as="6"] {
    font-size: var(--text-h6);
    line-height: var(--text-h6--line-height);
    font-family: var(--font-h6);
    font-weight: var(--font-weight-h6);
    margin-bottom: var(--spacing-h6);
  }
}
</style>
